import React from 'react';
import { Lock } from 'lucide-react';
import { usePlatformSettings } from '../../contexts/PlatformSettingsContext';

/**
 * PayRateGate — small helper used by every shift/job creation form.
 *
 * Iter173 (Feb 25, 2026): the super-admin has a platform-wide toggle
 * (`pay_rate_editing_enabled`) that switches per-job pay-rate editing
 * on and off across every creation surface (employer, agent, PM,
 * group-home). Rather than duplicate the disabled UX in every form,
 * wrap the rate input with this component:
 *
 *     <PayRateGate>
 *       <input value={form.pay_rate} onChange={...} />
 *     </PayRateGate>
 *
 * When the toggle is OFF, the child input renders `disabled` +
 * `readOnly` and a lock badge + explanatory tooltip appear over the
 * field. When ON, the child renders exactly as before. Consumers must
 * clone their input so we can inject `disabled`/`readOnly`/`title`
 * props without touching the parent form state.
 */
export default function PayRateGate({ children, className = '' }) {
  const { payRateEditingEnabled } = usePlatformSettings();
  if (payRateEditingEnabled) {
    return <>{children}</>;
  }
  // Inject disabled + readOnly on the direct child input/select so
  // the parent form doesn't need to know about the toggle.
  const child = React.Children.only(children);
  const gated = React.cloneElement(child, {
    disabled: true,
    readOnly: true,
    title: 'Pay-rate editing is currently disabled by the platform administrator.',
    'data-pay-rate-gated': 'true',
  });
  return (
    <div className={`relative ${className}`} data-testid="pay-rate-gate-disabled">
      {gated}
      <span
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"
        title="Pay-rate editing is currently disabled by the platform administrator."
      >
        <Lock className="w-3 h-3" /> Locked by admin
      </span>
    </div>
  );
}
